import React, { useState, useEffect, useContext } from "react";
import axios from 'axios';
import { useAPI, ViewContext, AuthContext } from 'components/lib';
import { Clock, AlertTriangle, CheckCircle, FileText, Edit3, ExternalLink, RefreshCw, MessageSquare, Archive, Eye, RotateCcw, ArrowLeft, ChevronDown } from 'lucide-react';
import moment from 'moment';
import styles from './RecordDetails.module.css';
import { isValidRedirect } from '../../utils/sanitize';

const RecordDetails = (props) => {
  const [currentEvent, setCurrentEvent] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [compareEvent, setCompareEvent] = useState(null);
  const [compareIndex, setCompareIndex] = useState(-1);
  const [eventList, setEventList] = useState([]);
  const [auditRuleStatus, setAuditRuleStatus] = useState([]);
  const [showRestoreDropdown, setShowRestoreDropdown] = useState(false);
  
  const context = useContext(ViewContext);
  const authContext = useContext(AuthContext);
  const [currentRealm] = useState(authContext.user.appConnection);
  const record = useAPI('/api/appData/record/' + props.id);

  useEffect(() => {
    async function fetchRecordDetails() {
      if (!props.id) return;
      
      let res = null;
      try {
        res = await axios({ 
          method: 'get', 
          url: '/api/appData/recordEvents/' + props.id, 
          data: { org_id: currentRealm, rec_id: props.id }
        });
      } catch(error) {
        // Failed to fetch record details
        return;
      }

      let recList = [];
      res.data.data.history.map((item) => {
        recList.push({
          id: item.id,
          operation: item.operation,
          lastUpdated: item.created_at,
          recDetails: item.rec_details,
          status: item.status,
          short_description: item.short_description,
          restore_id: item.restore_id,
          restore_to: item.restore_to
        });
        return item;
      });
      setEventList(recList);

      let statusList = [];
      res.data.data.status.map((item) => {
        statusList.push({
          id: item.id,
          result: item.result,
          created_at: item.created_at,
          short_description: item.short_description
        });
        return item;
      });
      setAuditRuleStatus(statusList);
    }

    fetchRecordDetails();
  }, [props.id, currentRealm]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showRestoreDropdown && !event.target.closest(`[class*="restoreDropdown"]`)) {
        setShowRestoreDropdown(false);
      }
    };

    if (showRestoreDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showRestoreDropdown]);

  const setActiveEvent = (rec, index) => {
    const { id, operation, lastUpdated } = rec;

    if(("Note" === operation) || ("Delete" === operation)) {
      return;
    }

    if(index === currentIndex) {
      setCurrentEvent(null);
      setCurrentIndex(-1);
      return;
    }

    setCurrentEvent({
      id,
      operation,
      lastUpdated,
      recDetails: rec.recDetails
    });

    setCurrentIndex(index);
  };

  const setActiveCompareEvent = (rec, index) => {
    const { id, operation, lastUpdated } = rec;

    if(("Note" === operation) || ("Delete" === operation)) {
      return;
    }

    if(currentIndex === index) {
      return;
    }
    if(index === compareIndex) {
      setCompareEvent(null);
      setCompareIndex(-1);
      return;
    }
    setCompareEvent({
      id,
      operation,
      lastUpdated,
      recDetails: rec.recDetails
    });

    setCompareIndex(index);
  };

  const rollbackToEvt = (evt) => {
    const versionNumber = eventList.length - eventList.findIndex(e => e.id === evt.id);
    const versionText = `Version #${versionNumber} (${evt.operation} from ${moment(evt.lastUpdated).format('MMM D, YYYY [at] h:mm A')})`;
    
    context.modal.show({
      title: 'Restore Record Version',
      form: {
        restore_notes: {
          label: 'Restoration Notes (Optional)',
          type: 'textarea',
          placeholder: 'Add notes about why you are restoring to this version...',
          value: `Restoring to ${versionText}`,
          rows: 3
        }
      },
      buttonText: 'Restore Version',
      url: '/api/appData/restore_rec/' + currentRealm + '/' + props.id + '/' + evt.id,
      method: 'POST',
      text: `Are you sure you want to restore this record to ${versionText}?`,
    }, async () => {
      context.notification.show('Your data is being restored now.', 'success', true, 'toast', 'heart');
    });
  };

  const clearRule = (rec) => {
    context.modal.show({
      title: 'Ignore Alert',
      form: {
        id: { type: 'hidden', value: rec.id },
        ignore: { type: 'hidden', value: true },
        note: { label: 'Notes', type: 'textarea', value: 'Rule ignored: ' },
        record_id: { type: 'hidden', value: props.id }
      },
      buttonText: 'Ignore Alert',
      url: '/api/auditRule/clearRule',
      method: 'POST'
    }, (res) => {
      context.notification.show('This alert will now be ignored.', 'success', true);
    });
  };

  const deeplinkToRec = () => {
    if (record?.data?.url) {
      // Validate URL before opening - allow external URLs for deeplinks
      if (isValidRedirect(record.data.url, true)) {
        window.open(record.data.url);
      } else {
        console.warn('Invalid deeplink URL blocked:', record.data.url);
      }
    }
  };

  // Helper function to flatten and simplify JSON data for user-friendly display
  const flattenDataForDisplay = (obj, prefix = '', result = {}) => {
    if (!obj || typeof obj !== 'object') {
      return result;
    }

    Object.keys(obj).forEach(key => {
      if (key.startsWith('_') || key === 'MetaData' || key === 'CustomField') {
        return; // Skip internal fields
      }

      const newKey = prefix ? `${prefix}.${key}` : key;
      const value = obj[key];

      if (value === null || value === undefined) {
        return; // Skip null/undefined values
      }

      if (Array.isArray(value)) {
        if (value.length === 0) return; // Skip empty arrays
        
        // For arrays, show count and first few items
        result[newKey] = `${value.length} item(s)`;
        value.slice(0, 3).forEach((item, index) => {
          if (typeof item === 'object') {
            flattenDataForDisplay(item, `${newKey}[${index}]`, result);
          } else {
            result[`${newKey}[${index}]`] = item;
          }
        });
      } else if (typeof value === 'object') {
        // For objects, check if it has common fields like Name, Amount, etc.
        const displayFields = ['Name', 'DisplayName', 'name', 'Amount', 'amount', 'Description', 'description', 'Id', 'id'];
        const hasDisplayField = displayFields.some(field => value[field] !== undefined);
        
        if (hasDisplayField) {
          const displayValue = displayFields.find(field => value[field] !== undefined);
          result[newKey] = value[displayValue];
        }
        
        // Recursively flatten important nested fields
        flattenDataForDisplay(value, newKey, result);
      } else {
        // Simple values
        result[newKey] = value;
      }
    });

    return result;
  };

  // Compare two flattened objects and return changes
  const compareData = (oldData, newData) => {
    const oldFlat = flattenDataForDisplay(oldData);
    const newFlat = flattenDataForDisplay(newData);
    const changes = {};

    // Find changed fields
    Object.keys(newFlat).forEach(key => {
      if (oldFlat[key] !== newFlat[key]) {
        changes[key] = {
          old: oldFlat[key],
          new: newFlat[key],
          changed: true
        };
      }
    });

    // Find added fields
    Object.keys(newFlat).forEach(key => {
      if (!(key in oldFlat)) {
        changes[key] = {
          old: null,
          new: newFlat[key],
          changed: true
        };
      }
    });

    // Add unchanged fields for context
    Object.keys(newFlat).forEach(key => {
      if (!(key in changes)) {
        changes[key] = {
          old: newFlat[key],
          new: newFlat[key],
          changed: false
        };
      }
    });

    return changes;
  };

  // Get changed fields between two events for timeline display
  const getChangedFieldsForTimeline = (currentEvent, previousEvent) => {
    if (!currentEvent || !previousEvent || !currentEvent.recDetails || !previousEvent.recDetails) {
      return [];
    }
    
    const currentData = currentEvent.recDetails;
    const previousData = previousEvent.recDetails;
    
    // Use the same comparison logic as the main compareData function
    const changes = compareData(previousData, currentData);
    const changedFields = [];
    
    // Get only the changed fields
    Object.entries(changes).forEach(([key, change]) => {
      if (change.changed) {
        changedFields.push({
          field: key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()),
          oldValue: change.old,
          newValue: change.new
        });
      }
    });
    
    return changedFields.slice(0, 5); // Limit to 5 changes for timeline display
  };

  const getEventIcon = (operation) => {
    switch(operation) {
      case 'Create': return <FileText className={styles.eventIcon} />;
      case 'Update': return <Edit3 className={styles.eventIcon} />;
      case 'Note': return <MessageSquare className={styles.eventIcon} />;
      case 'Delete': return <Archive className={styles.eventIcon} />;
      default: return <RefreshCw className={styles.eventIcon} />;
    }
  };

  const getEventDotClass = (operation) => {
    switch(operation) {
      case 'Create': return styles.create;
      case 'Update': return styles.update;
      case 'Note': return styles.note;
      case 'Delete': return styles.delete;
      default: return '';
    }
  };

  const renderStatusBadge = (status) => {
    if (!status || status === 'ok') return null;
    
    const badgeClass = status === 'warning' ? styles.warning : 
                     status === 'deleted' ? styles.deleted : styles.error;
    
    return (
      <span className={`${styles.statusBadge} ${badgeClass}`}>
        {status === 'warning' && <AlertTriangle className={styles.actionIcon} />}
        {status}
      </span>
    );
  };

  if (!props.id) {
    return (
      <div className={styles.emptyState}>
        <Eye className={styles.emptyStateIcon} />
        <h3 className={styles.emptyStateTitle}>No Record Selected</h3>
        <p className={styles.emptyStateText}>Please select a transaction to view its details and history</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        {/* Breadcrumb Navigation */}
        <div className={styles.breadcrumbContainer}>
          <button className={styles.backButton} onClick={props.onClose}>
            <ArrowLeft className={styles.backIcon} />
            Back to Records
          </button>
          <div className={styles.headerActions}>
            {record?.data?.url && (
              <button className={styles.actionButton} onClick={deeplinkToRec}>
                <ExternalLink className={styles.actionIcon} />
                Edit in System
              </button>
            )}
            {eventList.filter(evt => evt.operation !== 'Note' && evt.operation !== 'Delete').length > 1 && (
              <div className={styles.restoreDropdown}>
                <button 
                  className={styles.actionButton}
                  onClick={() => setShowRestoreDropdown(!showRestoreDropdown)}
                >
                  <RotateCcw className={styles.actionIcon} />
                  Restore Version
                  <ChevronDown className={styles.dropdownIcon} />
                </button>
                {showRestoreDropdown && (
                  <div className={styles.restoreDropdownMenu}>
                    <div className={styles.dropdownHeader}>
                      <span>Select version to restore:</span>
                    </div>
                    {eventList
                      .filter(evt => evt.operation !== 'Note' && evt.operation !== 'Delete')
                      .slice(1) // Skip the first (current/latest) version
                      .map((evt, index) => (
                        <button
                          key={evt.id}
                          className={styles.restoreOption}
                          onClick={() => {
                            rollbackToEvt(evt);
                            setShowRestoreDropdown(false);
                          }}
                        >
                          <div className={styles.restoreOptionContent}>
                            <div className={styles.restoreVersionInfo}>
                              <span className={styles.versionNumber}>
                                Version #{eventList.length - (index + 1)}
                              </span>
                              <span className={styles.operationType}>{evt.operation}</span>
                            </div>
                            <div className={styles.restoreTimestamp}>
                              {moment(evt.lastUpdated).format('MMM D, YYYY [at] h:mm A')}
                            </div>
                          </div>
                        </button>
                      ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
 
      </div>

      <div className={styles.body}>
        <div className={styles.leftPanel}>
          {/* Current Issues Section */}
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>
              <AlertTriangle className={styles.sectionIcon} />
              Current Issues
            </h2>
            
            {auditRuleStatus && auditRuleStatus.length > 0 ? (
              <div className={styles.alertsGrid}>
                {auditRuleStatus.map((rec) => {
                  return (
                    <div key={rec.id} className={`${styles.alertItem} ${rec.result === 'warning' ? styles.warning : ''}`}>
                      <AlertTriangle className={`${styles.alertIcon} ${rec.result === 'warning' ? styles.warning : ''}`} />
                      <div className={styles.alertContent}>
                        <p className={styles.alertText}>{rec.short_description}</p>
                      </div>
                      <button 
                        className={styles.ignoreButton}
                        onClick={() => clearRule(rec)}
                      >
                        Ignore
                      </button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className={styles.noAlerts}>
                <CheckCircle className={styles.emptyStateIcon} />
                <p>No issues found</p>
              </div>
            )}
          </div>

          {/* Record Summary Section */}
          {props.record && props.record.rec_normalized && (
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>
                <FileText className={styles.sectionIcon} />
                Record Summary
              </h2>
              <div className={styles.transactionSummary}>
                {(() => {
                  const normalized = props.record.rec_normalized;
                  return (
                    <div className={styles.summaryGrid}>
                      {normalized.TxnDate && (
                        <div className={styles.summaryField}>
                          <span className={styles.summaryLabel}>Date</span>
                          <span className={styles.summaryValue}>
                            {moment(normalized.TxnDate).format('MMM D, YYYY')}
                          </span>
                        </div>
                      )}
                      {normalized.DocNumber && (
                        <div className={styles.summaryField}>
                          <span className={styles.summaryLabel}>Document #</span>
                          <span className={styles.summaryValue}>{normalized.DocNumber}</span>
                        </div>
                      )}
                      {normalized.TotalAmt && (
                        <div className={styles.summaryField}>
                          <span className={styles.summaryLabel}>Amount</span>
                          <span className={`${styles.summaryValue} ${styles.amount}`}>
                            ${parseFloat(normalized.TotalAmt).toFixed(2)}
                          </span>
                        </div>
                      )}
                      {normalized.PrivateNote && (
                        <div className={styles.summaryField}>
                          <span className={styles.summaryLabel}>Description</span>
                          <span className={styles.summaryValue}>{normalized.PrivateNote}</span>
                        </div>
                      )}
                      {normalized.CurrencyRef?.value && (
                        <div className={styles.summaryField}>
                          <span className={styles.summaryLabel}>Currency</span>
                          <span className={styles.summaryValue}>{normalized.CurrencyRef.value}</span>
                        </div>
                      )}
                      {normalized.CustomerRef?.name && (
                        <div className={styles.summaryField}>
                          <span className={styles.summaryLabel}>Customer</span>
                          <span className={styles.summaryValue}>{normalized.CustomerRef.name}</span>
                        </div>
                      )}
                      {normalized.VendorRef?.name && (
                        <div className={styles.summaryField}>
                          <span className={styles.summaryLabel}>Vendor</span>
                          <span className={styles.summaryValue}>{normalized.VendorRef.name}</span>
                        </div>
                      )}
                      {normalized.line_items && normalized.line_items.length > 0 && (
                        <div className={styles.summaryField}>
                          <span className={styles.summaryLabel}>Line Items</span>
                          <span className={styles.summaryValue}>
                            {normalized.line_items.length} item(s)
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
          )}

          {/* Line Items Section */}
          {props.record?.rec_normalized?.line_items && props.record.rec_normalized.line_items.length > 0 && (
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>
                <MessageSquare className={styles.sectionIcon} />
                Line Items
              </h2>
              <div className={styles.lineItemsTable}>
                <div className={styles.lineItemsHeader}>
                  <span>Description</span>
                  <span>Amount</span>
                  <span>Account</span>
                </div>
                {props.record.rec_normalized.line_items.map((item, index) => {
                  return (
                    <div key={index} className={styles.lineItem}>
                      <span className={styles.lineItemDescription}>
                        {item.Description || item.description || `Line ${index + 1}`}
                      </span>
                      <span className={styles.lineItemAmount}>
                        {item.Amount && `$${parseFloat(item.Amount).toFixed(2)}`}
                      </span>
                      <span className={styles.lineItemAccount}>
                        {item.AccountRef?.name || item.account_name || '-'}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Activity Timeline Section */}
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>
              <Clock className={styles.sectionIcon} />
              Activity Timeline
            </h2>
            
            {eventList && eventList.length > 0 ? (
              <div className={styles.timeline}>
                <div className={styles.timelineTrack}></div>
                {eventList.map((rec, index) => {
                  const m = moment(rec.lastUpdated);
                  const isSelected = index === currentIndex;
                  const isComparing = index === compareIndex;
                  const previousEvent = index < eventList.length - 1 ? eventList[index + 1] : null;
                  const changedFields = previousEvent ? getChangedFieldsForTimeline(rec, previousEvent) : [];
                  
                  return (
                    <div key={rec.id} className={styles.timelineItem}>
                      <div className={`${styles.timelineDot} ${getEventDotClass(rec.operation)}`}></div>
                      <div 
                        className={`${styles.timelineCard} ${isSelected ? styles.selected : ''} ${isComparing ? styles.comparing : ''}`}
                        onClick={() => setActiveEvent(rec, index)}
                      >
                        {/* Visual indicator badges */}
                        {isSelected && (
                          <div className={styles.timelineCardBadge}>
                            <span className={styles.selectedBadge}>Selected</span>
                          </div>
                        )}
                        {isComparing && (
                          <div className={styles.timelineCardBadge}>
                            <span className={styles.comparingBadge}>Comparing</span>
                          </div>
                        )}
                        <div className={styles.timelineCardHeader}>
                          <div className={styles.eventInfo}>
                            <div className={styles.eventType}>
                              {getEventIcon(rec.operation)}
                              {rec.operation}
                            </div>
                            {renderStatusBadge(rec.status)}
                          </div>
                          <div className={styles.eventTime}>
                            <Clock className={styles.actionIcon} />
                            {m.format('MMM D, YYYY [at] h:mm A')}
                          </div>
                        </div>
                        
                        {rec.operation === 'Note' && (
                          <p className={styles.eventDescription}>{rec.short_description}</p>
                        )}
                        
                        {changedFields.length > 0 && (
                          <div className={styles.changedFields}>
                            <div className={styles.changedFieldsTitle}>
                              <Edit3 className={styles.changeIcon} />
                              Fields Changed ({changedFields.length})
                            </div>
                            <div className={styles.fieldChanges}>
                              {changedFields.map((change, changeIndex) => {
                                return (
                                  <div key={changeIndex} className={styles.fieldChange}>
                                    <span className={styles.fieldName}>{change.field}</span>
                                    <span className={styles.fieldChangeDetail}>
                                      {change.oldValue ? `${change.oldValue} → ${change.newValue}` : `Added: ${change.newValue}`}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        
                        {rec.restore_to && (
                          <div className={styles.restoreInfo}>
                            Data restored to: <a href={`/client/tools/restore/details/${rec.restore_id}`} className={styles.restoreLink}>
                              {moment(rec.restore_to).format('MMM D, YYYY [at] h:mm A')}
                            </a>
                          </div>
                        )}
                        
                        {currentIndex !== -1 && rec.operation !== 'Note' && rec.operation !== 'Delete' && !isSelected && (
                          <div className={styles.eventActions}>
                            <button 
                              className={styles.compareButton}
                              onClick={(e) => {
                                e.stopPropagation();
                                setActiveCompareEvent(rec, index);
                              }}
                            >
                              <Eye className={styles.actionIcon} />
                              Compare
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className={styles.emptyState}>
                <Clock className={styles.emptyStateIcon} />
                <h3 className={styles.emptyStateTitle}>No Activity</h3>
                <p className={styles.emptyStateText}>No historical events found for this record</p>
              </div>
            )}
          </div>

        </div>

        {/* Right Panel - Event Details */}
        <div className={styles.rightPanel}>
          {currentEvent ? (
            <>
              <div className={styles.rightPanelHeader}>
                <h3 className={styles.rightPanelTitle}>
                  {compareEvent ? 'Comparing Changes' : 'Event Details'}
                </h3>
                <div className={styles.comparisonInfo}>
                  {compareEvent ? (
                    <div className={styles.comparisonLabels}>
                      <div className={styles.compareFromLabel}>
                        <span className={styles.compareLabel}>From:</span>
                        <span className={styles.compareEvent}>
                          {compareEvent.operation} - {moment(compareEvent.lastUpdated).format('MMM D, h:mm A')}
                        </span>
                      </div>
                      <div className={styles.compareToLabel}>
                        <span className={styles.compareLabel}>To:</span>
                        <span className={styles.compareEvent}>
                          {currentEvent.operation} - {moment(currentEvent.lastUpdated).format('MMM D, h:mm A')}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className={styles.singleEventInfo}>
                      <span className={styles.compareLabel}>Comparing to:</span>
                      <span className={styles.compareEvent}>
                        {eventList[currentIndex + 1] 
                          ? `${eventList[currentIndex + 1].operation} - ${moment(eventList[currentIndex + 1].lastUpdated).format('MMM D, h:mm A')}`
                          : 'Previous version'
                        }
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <div className={styles.rightPanelContent}>
                {currentEvent.operation !== 'Clear' && (
                  <div className={styles.eventDetailsSection}>
                    <div className={styles.sectionHeader}>
                      <FileText className={styles.sectionHeaderIcon} />
                      <h4 className={styles.sectionHeaderTitle}>Record Data</h4>
                    </div>
                    {(() => {
                      // Always compare selected event to previous event, or user-selected compare event
                      const currentData = currentEvent.recDetails || {};
                      const compareEventData = compareEvent 
                        ? (compareEvent.recDetails || {})
                        : (eventList[currentIndex + 1]?.recDetails || {});
                      
                      const changes = compareData(compareEventData, currentData);
                      
                      // Separate changed and unchanged fields
                      const changedEntries = [];
                      const unchangedEntries = [];
                      
                      Object.entries(changes).forEach(([key, change]) => {
                        if (change.changed) {
                          changedEntries.push([key, change]);
                        } else {
                          unchangedEntries.push([key, change]);
                        }
                      });
                      
                      return [
                        // Show changed fields first
                        ...changedEntries.slice(0, 8).map(([key, change]) => (
                          <div key={key} className={styles.dataField}>
                            <div className={styles.dataLabel}>
                              {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                            </div>
                            <div className={styles.dataValue}>
                              <div className={styles.dataValueChanged}>
                                {change.new}
                                {change.old && (
                                  <div className={styles.dataValueOld}>
                                    Previous: {change.old}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )),
                        // Then show unchanged fields
                        ...unchangedEntries.slice(0, 7).map(([key, change]) => (
                          <div key={key} className={styles.dataField}>
                            <div className={styles.dataLabel}>
                              {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                            </div>
                            <div className={styles.dataValue}>
                              <span>{change.new}</span>
                            </div>
                          </div>
                        ))
                      ];
                    })()}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className={styles.noDataSelected}>
              <Eye className={styles.noDataIcon} />
              <p className={styles.noDataText}>
                Select an event from the timeline to view its details
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecordDetails;